/**
 * Trodo design tokens: the same palette as the Trodo app (app.trodo.ai, client/src/shared/theme/trodoColors.js), one
 * source for every colour in the console. Orange is the brand: primary actions, focus, links and selection. Surfaces
 * are neutral (no warm or blue tint), matte in dark mode. Keep this in step with the app's file.
 */

export const PRIMARY_HEX = '#ff6d29'
export const primaryHover = '#e55f1f'
export const PRIMARY_RGB = '255 109 41'

export const palette = {
  surface: {
    sunken: { light: '#f5f5f5', dark: '#0a0a0b' }, // sidebar rail, page ground behind cards
    base: { light: '#ffffff', dark: '#141415' }, // default page / card background
    raised: { light: '#ffffff', dark: '#1b1b1d' }, // popovers, dropdowns, modals
    overlay: { light: 'rgba(0,0,0,0.45)', dark: 'rgba(0,0,0,0.65)' },
    hover: { light: '#e8eaed', dark: '#232325' },
    selected: { light: '#fef3ec', dark: '#3a2418' },
    inverse: { light: '#0f172a', dark: '#f5f5f5' },
  },
  border: {
    subtle: { light: '#eef0f4', dark: '#202022' },
    default: { light: '#dde2ee', dark: '#2a2a2d' },
    strong: { light: '#c5cbda', dark: '#3a3a3e' },
    focus: { light: PRIMARY_HEX, dark: PRIMARY_HEX },
  },
  text: {
    primary: { light: '#0f172a', dark: '#f1f3f6' },
    secondary: { light: '#6b7280', dark: '#c8ccd4' },
    muted: { light: '#6b7280', dark: '#c8ccd4' },
    disabled: { light: '#a3a8b2', dark: '#7d838e' },
    inverse: { light: '#ffffff', dark: '#0f172a' },
    onBrand: { light: '#ffffff', dark: '#ffffff' },
    deep: { light: '#020617', dark: '#ffffff' },
  },
  state: {
    hoverBg: { light: 'rgba(0,0,0,0.04)', dark: 'rgba(255,255,255,0.06)' },
    activeBg: { light: 'rgba(0,0,0,0.08)', dark: 'rgba(255,255,255,0.10)' },
    selectedBg: { light: 'rgba(255,109,41,0.10)', dark: 'rgba(255,109,41,0.18)' },
    focusRing: { light: 'rgba(255,109,41,0.35)', dark: 'rgba(255,109,41,0.45)' },
  },
  feedback: {
    success: { light: '#16a34a', dark: '#22c55e' },
    successBg: { light: '#ecfdf5', dark: '#0f2a1c' },
    warning: { light: '#d97706', dark: '#f59e0b' },
    warningBg: { light: '#fffbeb', dark: '#2a1f0a' },
    error: { light: '#dc2626', dark: '#ef4444' },
    errorBg: { light: '#fef2f2', dark: '#2a1414' },
    info: { light: '#2563eb', dark: '#3b82f6' },
    infoBg: { light: '#eff6ff', dark: '#0f1f3a' },
  },
  brand: {
    primary: { light: PRIMARY_HEX, dark: PRIMARY_HEX },
    primaryHover: { light: primaryHover, dark: primaryHover },
    primarySoft: { light: '#fff2ec', dark: '#3a2418' },
  },
  shadow: {
    sm: { light: '0 1px 2px rgba(0,0,0,0.05)', dark: '0 1px 2px rgba(0,0,0,0.4)' },
    md: { light: '0 2px 8px rgba(0,0,0,0.05)', dark: '0 2px 8px rgba(0,0,0,0.5)' },
    lg: { light: '0 4px 16px rgba(0,0,0,0.10)', dark: '0 4px 16px rgba(0,0,0,0.6)' },
  },
  scrollbar: {
    thumb: { light: 'rgba(0,0,0,0.15)', dark: 'rgba(255,255,255,0.18)' },
    thumbHover: { light: 'rgba(0,0,0,0.22)', dark: 'rgba(255,255,255,0.28)' },
  },
}

function camelToKebab(name: string): string {
  return name.replace(/([A-Z])/g, '-$1').toLowerCase()
}

function hexToRgbChannels(value: string): string | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value).trim())
  if (!m) return null
  let hex = m[1]!
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
  const n = parseInt(hex, 16)
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
}

function flattenPalette(): [string, string, string][] {
  const out: [string, string, string][] = []
  for (const [group, tokens] of Object.entries(palette)) {
    for (const [name, value] of Object.entries(tokens)) {
      const cssVar = `--${group}-${camelToKebab(name)}`
      out.push([cssVar, (value as { light: string }).light, (value as { dark: string }).dark])
    }
  }
  return out
}

function flattenChannels(): [string, string, string][] {
  return flattenPalette()
    .map(([cssVar, light, dark]) => {
      const l = hexToRgbChannels(light)
      const d = hexToRgbChannels(dark)
      return l && d ? ([`${cssVar}-rgb`, l, d] as [string, string, string]) : null
    })
    .filter((x): x is [string, string, string] => x !== null)
}

export function applyTrodoPrimaryCssVars(): void {
  if (typeof document === 'undefined') return
  const triples = [...flattenPalette(), ...flattenChannels()]
  const lightDecls = triples.map(([v, l]) => `  ${v}: ${l};`).join('\n')
  const darkDecls = triples.map(([v, , d]) => `  ${v}: ${d};`).join('\n')

  const legacyLight = `
  --trodo-primary: ${PRIMARY_HEX};
  --trodo-primary-rgb: ${PRIMARY_RGB};
  --trodo-primary-hover: ${primaryHover};
  --trodo-ink: ${palette.text.primary.light};
  --trodo-grey: ${palette.text.secondary.light};
  --trodo-grey-light: ${palette.text.muted.light};`

  const legacyDark = `
  --trodo-ink: ${palette.text.primary.dark};
  --trodo-grey: ${palette.text.secondary.dark};
  --trodo-grey-light: ${palette.text.muted.dark};`

  const css = `
:root {
${lightDecls}
${legacyLight}
}
:root[data-theme="dark"] {
${darkDecls}
${legacyDark}
}`

  let style = document.getElementById('trodo-tokens')
  if (!style) {
    style = document.createElement('style')
    style.id = 'trodo-tokens'
    document.head.appendChild(style)
  }
  style.textContent = css
}
